import React, { useState, useEffect, useCallback, useId } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EntityPicker, complianceRoleLabel, ccoErrorText } from './adminCco';
import { ROLE_DEFAULTS } from './roleDefaults';
import { GOLD, NAVY_FORM, NAVY_SHELL } from './colors';
import { NavGroup, useConfirm, ScrollableTable, LoadError } from './ui';
// ADMINTOAST: the app-wide toast system (mounted by ToastProvider in index.js, so this component
// is always inside it). Replaces AdminPanel's native alert() dialogs, the last screen still
// using them after CONFIRMMODAL converted the window.confirm calls.
import { useToast } from './ToastContext';
import ValidationView from './ValidationView';
import EmployeeView from './EmployeeView';
import { consumePostLoginNext, peekPostLoginNext } from './postLoginNext';
import mkT from './theme';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';

// AUDITPAGE: rows fetched per audit-log request. Matches the backend's own default page size and
// stays well under its 500 hard ceiling.
const LOG_PAGE_SIZE = 100;

function TopbarBtn({ children, onClick }) {
  const [h, setH] = React.useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ backgroundColor: h ? 'rgba(201,168,76,0.15)' : 'transparent', color: h ? GOLD : '#fff', border: `0.5px solid ${h ? GOLD : 'rgba(255,255,255,0.25)'}`, borderRadius: '6px', padding: '6px 12px', fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s', fontWeight: h ? 500 : 400 }}>
      {children}
    </button>
  );
}


// ADMINPAGES: nav config + URL map. Scheduler is deliberately last (confirmed decision).
const NAV_ITEMS = [
  { id: 'users',     label: 'User accounts', path: '/admin' },
  { id: 'audit',     label: 'Audit log',     path: '/admin/audit-log' },
  // JOBLOG: read-only recent scheduled-job runs, so a failed/partial run is noticed here rather
  // than only in journalctl.
  { id: 'jobruns',   label: 'Job runs',      path: '/admin/job-runs' },
  { id: 'scheduler', label: 'Scheduler',     path: '/admin/scheduler' },
];
const URL_TO_SECTION = {
  '/admin': 'users',
  '/admin/audit-log': 'audit',
  '/admin/job-runs': 'jobruns',
  '/admin/scheduler': 'scheduler',
  '/admin/validation': 'validation',
  '/admin/my-profile': 'profile',
};
// FPFIX: the admin gets the COO's Validation page (the F&P review chain included) and, when the
// account has its own employee record, its My Profile, where its own attestations and F&P
// questionnaire live and where the email links land. Inserted before Scheduler, which stays last.
const VALIDATION_NAV = { id: 'validation', labelKey: 'adminNav.validation', path: '/admin/validation' };
const PROFILE_NAV = { id: 'profile', labelKey: 'adminNav.myProfile', path: '/admin/my-profile' };
const T = mkT(false);   // light mode only, per the confirmed decision

// ADMINPAGES: the 3 admin pages. Deliberately presentational - they own NO state and make NO API
// calls. Every piece of state and all 13 API calls stay lifted in AdminPanel below, exactly where
// they were before this split, so loadData() still runs once on mount and after every mutation.
// Props are passed through unchanged; the JSX inside each page is the original section verbatim.

function UserAccountsPage(p) {
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const { s, users, showCreateForm, setShowCreateForm, createForm, setCreateForm, createLoading,
          handleCreateAccount, actionLoading, handleResetPassword, handleResendWelcome,
          handleResendWelcomeCompany,
          handleToggleActive, handleResetMfa, handleChangeRole, handleExpandUser, expandedUser,
          delegateEmail, setDelegateEmail, savingDelegate, handleSaveDelegateEmail,
          resendingDelegate, handleResendDelegateNotification, fmt,
          ccoT, complianceEntities, createEntities, setCreateEntities, scopeEditor, setScopeEditor,
          saveScopeEditor, scopeSaving, handleEditCompliance } = p;
  return (
    <>
      <div style={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ ...s.sectionTitle, margin: 0 }}>User accounts</h2>
          <button style={s.btn} onClick={() => setShowCreateForm(v => !v)}>
            {showCreateForm ? 'Cancel' : '+ Create account'}
          </button>
        </div>
        {showCreateForm && (
          <form onSubmit={handleCreateAccount} style={s.createForm}>
            <div style={{ flex: 1 }}>
              <label style={s.label} htmlFor={`${a11yId}-email`}>Email</label>
              
              <input id={`${a11yId}-email`}
                type="email"
                required
                value={createForm.email}
                onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
                style={s.input}
                placeholder="user@enkocapital.com"
              />
            </div>
            <div>
              <label style={s.label} htmlFor={`${a11yId}-role`}>Role</label>
              
              <select id={`${a11yId}-role`}
                value={createForm.role}
                onChange={e => setCreateForm(f => ({ ...f, role: e.target.value }))}
                style={s.input}
              >
                <option value="employee">Employee</option>
                <option value="ceo">CEO</option>
                <option value="technology">Technology</option>
                <option value="hr">HR</option>
                <option value="compliance">Compliance</option>
                <option value="finance">Financial Controller</option>
                {/* PAYSPLIT: per-entity payroll contact - Payroll workflow + Leaver Section 3
                    only, entity-scoped server-side via the payroll-contact resolution. */}
                <option value="payroll">Payroll Contact</option>
                <option value="coo">COO</option>
                <option value="admin">Admin</option>
              </select>
              {/* CCO: a compliance account is created with the entities it covers; none makes it the CCO. */}
              {createForm.role === 'compliance' && (
                <EntityPicker entities={complianceEntities} selected={createEntities}
                              onChange={setCreateEntities} t={ccoT} idPrefix={`${a11yId}-create-entity`} />
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button type="submit" style={s.btn} disabled={createLoading}>
                {createLoading ? '…' : 'Create'}
              </button>
            </div>
          </form>
        )}
        {users.length === 0 ? (
          <p style={s.empty}>No user accounts found.</p>
        ) : (
          <ScrollableTable><table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Email</th>
                <th style={s.th}>Role</th>
                <th style={s.th}>Status</th>
                {/* MFACOLUMN: GET /admin/users already returned mfa_enabled/mfa_pending, but
                    nothing displayed them - the fields were fetched and discarded. Without
                    this column the only MFA signal was the Reset MFA button's visibility,
                    which could not distinguish "never enrolled" from "enrolled". */}
                <th style={s.th}>MFA</th>
                <th style={s.th}>Last login</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <React.Fragment key={user.id}>
                  <tr style={s.tr}>
                    <td style={s.td}>{user.email}</td>
                    <td style={s.td}>
                      <select
                        style={{ ...s.roleSelect, ...roleColor(user.role) }}
                        value={user.role || 'employee'}
                        disabled={actionLoading === user.email + ':role'}
                        onChange={e => handleChangeRole(user.email, e.target.value, user.role || 'employee')}
                      >
                        <option value="employee">Employee</option>
                        <option value="ceo">CEO</option>
                        <option value="technology">Technology</option>
                        <option value="hr">HR</option>
                        <option value="compliance">Compliance</option>
                        <option value="finance">Financial Controller</option>
                        <option value="payroll">Payroll Contact</option>
                        <option value="coo">COO</option>
                        <option value="admin">Admin</option>
                      </select>
                      {/* CCO: which compliance account is the CCO, the entities of the others, and
                          the control that edits them. */}
                      {user.role === 'compliance' && (
                        <div data-testid={`cco-role-${user.email}`} style={{ marginTop: '4px', fontSize: '11px' }}>
                          {complianceRoleLabel(user, ccoT)}
                          {' '}
                          <button type="button" style={{ ...s.actionBtn, fontSize: '11px', padding: '2px 6px' }}
                                  onClick={() => handleEditCompliance(user)}>
                            {ccoT('adminCco.editEntities')}
                          </button>
                        </div>
                      )}
                      {scopeEditor && scopeEditor.email === user.email && (
                        <div data-testid="cco-scope-editor" style={{ marginTop: '6px' }}>
                          <EntityPicker entities={complianceEntities} selected={scopeEditor.selected}
                                        onChange={sel => setScopeEditor(ed => ({ ...ed, selected: sel }))}
                                        t={ccoT} idPrefix={`${a11yId}-scope-${user.id}`} />
                          <button type="button" style={s.btn} disabled={scopeSaving} onClick={saveScopeEditor}>
                            {scopeSaving ? ccoT('adminCco.saving') : ccoT('adminCco.save')}
                          </button>
                          {' '}
                          <button type="button" style={s.actionBtn} disabled={scopeSaving}
                                  onClick={() => setScopeEditor(null)}>
                            {ccoT('adminCco.cancel')}
                          </button>
                        </div>
                      )}
                      {/* HRSHELL point 7: an hr account with NO linked employee record is broken
                          in three specific ways, and nothing told the admin so. My Profile,
                          Tasks and Leave all resolve the caller's OWN employee id: GET
                          /employees/me returns 404 without one, so My Profile renders "No profile
                          found", GET /dashboard/my-tasks has no id to filter on, and the leave
                          calendar's scope_for() cannot resolve a scope.

                          THIS CHANGES NOTHING ABOUT HOW ACCOUNTS ARE CREATED, as the task
                          requires. It is a warning on an existing row, shown only for the role it
                          applies to. The fix is to link the record, which is done where employees
                          are created, not here.

                          Deliberately scoped to hr rather than shown for every role: a
                          compliance, finance or technology account without a record has a
                          different and smaller problem, and widening this would be a change the
                          task did not ask for. */}
                      {user.role === 'hr' && !user.employee_id && (
                        <div style={{ marginTop: '4px', fontSize: '11px', color: '#A63A3A' }}>
                          No employee record: My Profile, Tasks and Leave will not work
                        </div>
                      )}
                    </td>
                    <td style={s.td}>
                      {user.is_active
                        ? <span style={s.activeChip}>Active</span>
                        : <span style={s.inactiveChip}>Inactive</span>}
                    </td>
                    {/* MFACOLUMN: 3 states from the 2 booleans the API already returns.
                        Order matters - mfa_enabled is checked first because the backend
                        computes mfa_pending as (mfa_secret AND NOT mfa_enabled), so the two
                        are mutually exclusive, but checking enabled first is robust even if
                        that ever changes. */}
                    <td style={s.td}>
                      {user.mfa_enabled
                        ? <span style={s.mfaEnrolledChip}>Enrolled</span>
                        : user.mfa_pending
                          ? <span style={s.mfaPendingChip}>Pending verification</span>
                          : <span style={s.mfaNoneChip}>Not enrolled</span>}
                      <MfaDates user={user} />
                    </td>
                    <td style={s.td}>{fmt(user.last_login)}</td>
                    <td style={s.td}>
                      {/* PWWARN: all three password-overwriting controls are DISABLED for a
                          deactivated account. The BACKEND refuses them regardless - that is the
                          real control, and it is what a scripted caller hits - but disabling here
                          means the click cannot be made at all, and the title says why rather than
                          leaving the user to read an error toast. */}
                      <button
                        style={s.actionBtn}
                        disabled={actionLoading === user.email + ':reset' || user.is_active === false}
                        title={user.is_active === false ? 'This account is deactivated. Reactivate it first.' : undefined}
                        onClick={() => handleResetPassword(user.email)}
                      >
                        {actionLoading === user.email + ':reset' ? '…' : 'Reset password'}
                      </button>
                      {/* RESENDCOMPANY: the two resend buttons sit side by side, so each names its
                          DESTINATION rather than both reading "Resend welcome email". Only the
                          LABEL and title of this first one changed - its handler, its endpoint and
                          the personal-first resolution behind it are untouched. */}
                      <button
                        style={s.actionBtn}
                        disabled={actionLoading === user.email + ':welcome' || user.is_active === false}
                        onClick={() => handleResendWelcome(user.email)}
                        title={user.is_active === false
                          ? 'This account is deactivated. Reactivate it first.'
                          : 'Re-send the full welcome email with a new temporary password, to the personal address first (then delegate, then company)'}
                      >
                        {actionLoading === user.email + ':welcome' ? '…' : 'Resend welcome (personal)'}
                      </button>
                      <button
                        style={s.actionBtn}
                        disabled={actionLoading === user.email + ':welcomecompany' || user.is_active === false}
                        onClick={() => handleResendWelcomeCompany(user.email)}
                        /* MFAALWAYS: one title, because the action now behaves the same way for
                           every account - the enrolment-dependent wording is gone. */
                        title={user.is_active === false
                          ? 'This account is deactivated. Reactivate it first.'
                          : 'Send the welcome email with a new temporary password to the company address only. MFA is kept unless you choose to reset it. No fallback.'}
                      >
                        {actionLoading === user.email + ':welcomecompany' ? '…' : 'Welcome company'}
                      </button>
                      <button
                        style={{
                          ...s.actionBtn,
                          ...(user.is_active ? s.deactivateBtn : s.activateBtn),
                        }}
                        disabled={actionLoading === user.email + ':toggle'}
                        onClick={() => handleToggleActive(user.email)}
                      >
                        {actionLoading === user.email + ':toggle'
                          ? '…'
                          : user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      {/* MFACOLUMN: was `mfa_enabled || mfa_pending`, which showed this
                          button for a pending-verification account too. That was actively
                          misleading: a pending account's QR is still valid (setup_mfa reuses
                          the stored secret), so resetting needlessly destroys a working
                          enrolment and forces a re-scan. Their real blocker is the password,
                          not MFA. Now only a genuinely enrolled account can be reset. */}
                      {/* MFANOINACTIVE: disabled for a deactivated account, matching the three
                          password controls above. The BACKEND refuses regardless (the real
                          control, and what a scripted caller hits); this stops the click being
                          made and says why. The button is still only RENDERED for a genuinely
                          enrolled account, which MFACOLUMN decided and this does not change. */}
                      {user.mfa_enabled && (
                        <button
                          style={{ ...s.actionBtn, ...s.resetMfaBtn }}
                          disabled={actionLoading === user.email + ':mfa' || user.is_active === false}
                          title={user.is_active === false ? 'This account is deactivated. Reactivate it first.' : undefined}
                          onClick={() => handleResetMfa(user.email)}
                        >
                          {actionLoading === user.email + ':mfa' ? '…' : 'Reset MFA'}
                        </button>
                      )}
                      {(user.role === 'employee' || user.role === 'ceo') && (
                        <button
                          style={{ ...s.actionBtn, marginRight: 0 }}
                          onClick={() => handleExpandUser(user)}
                        >
                          {expandedUser === user.email ? 'Close' : 'Edit'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedUser === user.email && (user.role === 'employee' || user.role === 'ceo') && (
                    <tr>
                      {/* MFACOLUMN: 5 -> 6, matching the new MFA column. Without this the
                          expanded Edit panel would not span the full table width. */}
                      <td colSpan={6} style={{ padding: '10px 16px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <label style={{ ...s.label, margin: 0, whiteSpace: 'nowrap' }} htmlFor={`${a11yId}-delegate-email`}>Delegate email</label>
                          
                          <input id={`${a11yId}-delegate-email`}
                            type="text"
                            value={delegateEmail}
                            onChange={e => setDelegateEmail(e.target.value)}
                            placeholder="email@enkocapital.com"
                            style={{ ...s.input, minWidth: '240px', fontSize: '13px', padding: '6px 10px' }}
                          />
                          <button
                            style={{ ...s.btn, padding: '7px 18px', fontSize: '13px' }}
                            disabled={savingDelegate || !user.employee_id}
                            onClick={() => handleSaveDelegateEmail(user)}
                          >
                            {savingDelegate ? '…' : 'Save'}
                          </button>
                          {delegateEmail && (
                            <button
                              style={{ padding: '7px 18px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', color: '#888', background: 'transparent', cursor: resendingDelegate ? 'wait' : 'pointer' }}
                              disabled={resendingDelegate || !user.employee_id}
                              onClick={() => handleResendDelegateNotification(user)}
                            >
                              {resendingDelegate ? '…' : 'Resend notification'}
                            </button>
                          )}
                          {!user.employee_id && (
                            <span style={{ fontSize: '12px', color: '#aaa' }}>No employee record linked</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table></ScrollableTable>
        )}
      </div>

    </>
  );
}

function AuditLogPage(p) {
  const { s, logs, logTotal, logFilter, setLogFilter, logActionTypes, logsLoading, logsFailed, loadLogs } = p;
  return (
    <>
      <div style={s.section}>
        <h2 style={s.sectionTitle}>
          Audit log
          {logTotal > 0 && <span style={s.badge}>{logTotal}</span>}
        </h2>

        {/* AUDITPAGE: filter row. Changing the filter always restarts from offset 0. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', margin: '0 0 16px 0' }}>
          <label style={{ fontSize: '13px', color: '#666' }} htmlFor="log-filter">Action</label>
          <select
            id="log-filter"
            value={logFilter}
            onChange={e => { const f = e.target.value; setLogFilter(f); loadLogs(f, 0); }}
            style={{ ...s.input, minWidth: '220px', fontSize: '13px', padding: '6px 10px' }}
          >
            <option value="exclude-login">All except login (default)</option>
            <option value="all">All (including login)</option>
            {logActionTypes.map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          {logTotal > 0 && (
            <span style={{ fontSize: '13px', color: '#888' }}>
              Showing {logs.length} of {logTotal}
            </span>
          )}
        </div>

        {logsFailed ? (
          // AUDITLOGFAILSAFE: checked before the empty-state branch, so a failed fresh load can
          // never render as "No audit log entries for this filter." on the security audit trail.
          <LoadError message="Could not load the audit log." onRetry={() => loadLogs(logFilter, 0)} />
        ) : logs.length === 0 ? (
          <p style={s.empty}>
            {logsLoading ? 'Loading...' : 'No audit log entries for this filter.'}
          </p>
        ) : (
          <>
            <ScrollableTable><table style={s.table}>
              <thead>
                <tr>
                  {Object.keys(logs[0]).map(k => (
                    <th key={k} style={s.th}>{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((row, i) => (
                  <tr key={i} style={s.tr}>
                    {Object.values(row).map((v, j) => (
                      <td key={j} style={s.logCell}>{String(v ?? '-')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table></ScrollableTable>
            {logs.length < logTotal && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <button
                  style={{ ...s.btn, opacity: logsLoading ? 0.6 : 1 }}
                  disabled={logsLoading}
                  onClick={() => loadLogs(logFilter, logs.length, { append: true })}
                >
                  {logsLoading ? 'Loading...' : `Load more (${logTotal - logs.length} older)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

    </>
  );
}

function SchedulerPage(p) {
  const { s, schedulerStatus, handleSchedulerTrigger } = p;
  return (
    <>
      {/* ADMINPAGES: confirmed placement - Scheduler is last in the nav and carries this
          warning, because every button below fires a real job against live data and sends real
          emails. It is not a dry run. */}
      <div style={{ backgroundColor: '#fffbea', border: '1px solid #d4ac0d', borderRadius: '8px',
                    padding: '12px 16px', marginBottom: '16px', color: '#7d6608', fontSize: '13px' }}>
        <strong>These actions affect live data.</strong> Each button runs the real scheduled job
        immediately against production data and sends real emails to real recipients. There is no
        dry-run mode.
      </div>

      <div style={s.section}>
        <h2 style={s.sectionTitle}>Scheduler</h2>
        <p style={{ fontSize: '13px', color: '#888', margin: '0 0 16px 0' }}>
          Manually trigger scheduled jobs. Each job runs against live data and sends real emails.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[
            { key: 'overdue',  label: 'Test overdue tasks',            path: '/admin/scheduler/test-overdue' },
            { key: 'reminder', label: 'Test reminders',                path: '/admin/scheduler/test-reminders' },
            { key: 'visa',     label: 'Test visa/passport reminders',  path: '/admin/scheduler/test-visa-reminders' },
            { key: 'fcaQuarterly', label: 'Create test FCA Quarterly instance', path: '/workflows/fca-quarterly/test-create', method: 'post' },
          ].map(({ key, label, path, method }) => {
            const st = schedulerStatus[key] || {};
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  style={{ ...s.btn, minWidth: '240px', textAlign: 'left' }}
                  disabled={st.loading}
                  onClick={() => handleSchedulerTrigger(key, path, method)}
                >
                  {st.loading ? '…' : label}
                </button>
                {st.result === 'ok' && (
                  <span style={{ fontSize: '13px', color: '#27ae60' }}>Done</span>
                )}
                {st.result === 'error' && (
                  <span style={{ fontSize: '13px', color: '#e74c3c' }}>{st.error}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// JOBLOG: read-only recent scheduled-job executions. Failed/partial runs are colour-flagged so a
// problem is noticed here instead of only in journalctl. Clicking a failed/partial row reveals its
// error_detail. Purely a monitoring view - it triggers nothing.
function JobRunsPage({ s, jobRuns, jobRunsLoading, jobRunsError, loadJobRuns, fmt }) {
  const [expanded, setExpanded] = React.useState(null);
  const statusStyle = (status) => {
    const base = { padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' };
    if (status === 'failed')  return { ...base, backgroundColor: '#fdeaea', color: '#c0392b' };  // red
    if (status === 'partial') return { ...base, backgroundColor: '#fdf6e3', color: '#b9770e' };  // amber
    if (status === 'running') return { ...base, backgroundColor: '#eef2f7', color: '#5a6b82' };
    return { ...base, backgroundColor: '#eaf3ee', color: '#1e8449' };                            // green success
  };
  const failedCount = jobRuns.filter(r => r.status === 'failed' || r.status === 'partial').length;
  return (
    <div style={s.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ ...s.sectionTitle, margin: 0 }}>Job runs</h2>
        <button style={{ ...s.btn, minWidth: 'auto', padding: '6px 12px' }} onClick={loadJobRuns} disabled={jobRunsLoading}>
          {jobRunsLoading ? '…' : 'Refresh'}
        </button>
      </div>
      <p style={{ fontSize: '13px', color: '#888', margin: '6px 0 16px 0' }}>
        Recent scheduled-job executions. Failed and partial runs are flagged in colour; click one to
        see the actual error. {failedCount > 0 ? `${failedCount} recent run(s) need attention.` : 'No recent failures.'}
      </p>
      {jobRunsError && <p style={{ fontSize: '13px', color: '#c0392b' }}>Could not load job runs.</p>}
      {!jobRunsError && !jobRunsLoading && jobRuns.length === 0 && (
        <p style={{ fontSize: '13px', color: '#888', fontStyle: 'italic' }}>No job runs recorded yet.</p>
      )}
      {jobRuns.length > 0 && (
        <ScrollableTable><table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Job</th>
              <th style={s.th}>Started</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Summary</th>
            </tr>
          </thead>
          <tbody>
            {jobRuns.map(run => {
              const hasError = run.status === 'failed' || run.status === 'partial';
              const isOpen = expanded === run.id;
              return (
                <React.Fragment key={run.id}>
                  <tr
                    style={{ ...s.tr, cursor: hasError ? 'pointer' : 'default' }}
                    onClick={() => hasError && setExpanded(isOpen ? null : run.id)}
                  >
                    <td style={s.td}><span style={{ fontWeight: 500, fontSize: '13px' }}>{run.job_name}</span></td>
                    <td style={s.td}><span style={{ fontSize: '12px', color: '#888' }}>{fmt ? fmt(run.started_at) : run.started_at}</span></td>
                    <td style={s.td}><span style={statusStyle(run.status)}>{run.status}</span></td>
                    <td style={s.td}>
                      <span style={{ fontSize: '13px' }}>{run.summary || '-'}</span>
                      {hasError && <span style={{ fontSize: '11px', color: '#c0392b', marginLeft: '8px' }}>{isOpen ? '(hide error)' : '(show error)'}</span>}
                    </td>
                  </tr>
                  {isOpen && run.error_detail && (
                    <tr>
                      <td style={{ ...s.td, background: '#fbf7f7' }} colSpan={4}>
                        <pre style={{ margin: 0, fontSize: '11px', color: '#7a2b2b', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '260px', overflow: 'auto' }}>
                          {run.error_detail}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table></ScrollableTable>
      )}
    </div>
  );
}

function AdminPanel({ currentUser, topBarIdentity, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  // ADMINPAGES: URL-driven, matching Dashboard.js's URL_TO_SECTION pattern, so refresh, direct
  // URL entry and browser back/forward all land on the right page. Scheduler is LAST by
  // confirmed decision.
  const activeSection = URL_TO_SECTION[location.pathname] || 'users';
  // CONFIRMMODAL: styled confirmation replacing this file's 3 native window.confirm calls.
  // AdminPanel has no dark mode, so the modal renders in its light variant.
  const [askConfirm, confirmModal] = useConfirm(false);
  // ADMINTOAST: same hook every other screen uses. Message text and the success/error
  // distinction are preserved exactly from the alert() calls this replaces.
  const showToast = useToast();
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // { email, tempPassword }
  const [actionLoading, setActionLoading] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', role: 'employee' });
  // CCO: the entities a compliance account can cover, the ones chosen at creation, and the open
  // editor ({ email, mode: 'role' | 'scope', selected }).
  const [complianceEntities, setComplianceEntities] = useState([]);
  const [createEntities, setCreateEntities] = useState([]);
  const [scopeEditor, setScopeEditor] = useState(null);
  const [scopeSaving, setScopeSaving] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [delegateEmail, setDelegateEmail] = useState('');
  const [savingDelegate, setSavingDelegate] = useState(false);
  const [resendingDelegate, setResendingDelegate] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState({});
  // JOBLOG: recent scheduled-job runs (read-only monitoring).
  const [jobRuns, setJobRuns] = useState([]);
  const [jobRunsLoading, setJobRunsLoading] = useState(false);
  // DUALWARN: configuration warnings from the server, as codes; worded here through i18n.
  const { t: adminT } = useTranslation();
  const [configWarnings, setConfigWarnings] = useState([]);
  // FPFIX: the admin account's own employee record, if it has one (My Profile and the email links).
  const [myEmployee, setMyEmployee] = useState(null);
  useEffect(() => {
    let live = true;
    axios.get(`${API}/employees/me`)
      .then(res => (res && res.data && res.data.id ? res.data : null))
      .catch(() => null)
      .then(me => {
        if (!live) return;
        setMyEmployee(me);
        // FPFIX: an emailed link. Validation opens Validation. Attestations and F&P open My Profile,
        // which consumes the signal and opens the right tab; with no employee record there is
        // nothing of the admin's own to open, so F&P opens the review page and attestations home.
        const next = peekPostLoginNext();
        if (next === 'validation') { consumePostLoginNext(); navigate(VALIDATION_NAV.path); }
        else if (['attestations', 'fp'].includes(next)) {
          if (me) navigate(PROFILE_NAV.path);
          else { consumePostLoginNext(); navigate(next === 'fp' ? VALIDATION_NAV.path : '/admin'); }
        }
      });
    return () => { live = false; };
  }, []); // eslint-disable-line
  useEffect(() => {
    let live = true;
    axios.get(`${API}/admin/config-warnings`)
      .then(res => { if (live) setConfigWarnings(Array.isArray(res.data?.warnings) ? res.data.warnings : []); })
      .catch(() => { if (live) setConfigWarnings([]); });
    return () => { live = false; };
  }, []);
  const [jobRunsError, setJobRunsError] = useState(false);
  // AUDITPAGE: audit log paging + filter state. LOG_PAGE_SIZE rows are fetched at a time and
  // appended via "Load more"; logTotal comes from the backend so "showing X of Y" reflects the
  // count for the CURRENT filter, not the whole table.
  const [logTotal, setLogTotal] = useState(0);
  const [logActionTypes, setLogActionTypes] = useState([]);
  // Default 'exclude-login': login events are the large majority of rows and crowd out the
  // administrative actions an audit review is looking for. Toggled back on by choosing
  // "All (including login)" or "login" in the filter dropdown - nothing is hidden permanently.
  const [logFilter, setLogFilter] = useState('exclude-login');
  const [logsLoading, setLogsLoading] = useState(false);
  // AUDITLOGFAILSAFE: distinct from `logsLoading`. A failed fresh load left `logs` at its
  // empty-array default, rendering "No audit log entries for this filter." on the security
  // audit trail. Only set on a fresh load (not `append`, i.e. "Load more"), since an append
  // failure leaves the already-loaded rows in place and should not blank the whole table.
  const [logsFailed, setLogsFailed] = useState(false);

  // AUDITPAGE: translate the single dropdown selection into the backend's two independent params.
  const logParams = (filter, offset) => {
    const params = { limit: LOG_PAGE_SIZE, offset };
    if (filter === 'exclude-login') params.exclude_action = 'login';
    else if (filter !== 'all') params.action = filter;
    return params;
  };

  // Kept separate from loadData so the many admin mutations that call loadData() do not reset
  // the operator's paging position or refetch the whole user list unnecessarily.
  const loadLogs = async (filter, offset, { append = false } = {}) => {
    setLogsLoading(true);
    if (!append) setLogsFailed(false);
    try {
      const res = await axios.get(`${API}/admin/logs`, { params: logParams(filter, offset) });
      const rows = res.data?.rows ?? [];
      setLogs(prev => (append ? [...prev, ...rows] : rows));
      setLogTotal(res.data?.total ?? 0);
    } catch (err) {
      if (!append) { setLogs([]); setLogTotal(0); setLogsFailed(true); }
    } finally {
      setLogsLoading(false);
    }
  };

  // useCallback with an empty dep array: loadData reads nothing from state (the filter arrives as
  // an argument), so its identity is stable and the mount-time effect below can depend on it
  // honestly rather than suppressing the exhaustive-deps rule.
  const loadData = useCallback(async (filter = 'exclude-login') => {
    setError(null);
    try {
      const [usersRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        // Filter passed in rather than read from state, so this function does not close over
        // logFilter (which would make the mount-time useEffect below depend on it and refetch
        // the user list on every filter change).
        axios.get(`${API}/admin/logs`, { params: logParams(filter, 0) }),
      ]);
      setUsers(usersRes.data);
      setLogs(logsRes.data?.rows ?? []);
      setLogTotal(logsRes.data?.total ?? 0);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      setError(`Error ${status ?? 'network'}: ${detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  // CCO: the entities a compliance account can cover. Fails soft: no list, no checkboxes.
  useEffect(() => {
    try {
      Promise.resolve(axios.get(`${API}/admin/compliance-entities`))
        .then(res => setComplianceEntities(res?.data?.entities || []))
        .catch(() => setComplianceEntities([]));
    } catch (e) {
      setComplianceEntities([]);
    }
  }, []);

  // JOBLOG: fetch recent job runs. Read-only; own state so a failure degrades this page only.
  const loadJobRuns = useCallback(async () => {
    setJobRunsLoading(true);
    setJobRunsError(false);
    try {
      const res = await axios.get(`${API}/admin/job-executions`, { params: { limit: 50 } });
      setJobRuns(res.data?.rows ?? []);
    } catch (err) {
      setJobRuns([]);
      setJobRunsError(true);
    } finally {
      setJobRunsLoading(false);
    }
  }, []);

  useEffect(() => { if (activeSection === 'jobruns') loadJobRuns(); }, [activeSection, loadJobRuns]);

  // Populate the filter dropdown from the actions actually present, so it never goes stale.
  useEffect(() => {
    axios.get(`${API}/admin/logs/action-types`)
      .then(res => setLogActionTypes(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  // PWWARN: Reset password had NO confirm dialog at all - it fired on the first click. It is the
  // third control that overwrites the password, so it now asks, and carries the same enrolled-user
  // warning as the two resends. For an account that has never signed in the dialog is a plain
  // confirmation; the destructive case is the one that gets the warning.
  const handleResetPassword = (email) => {
    const warning = pwOverwriteWarning(email);
    askConfirm({
      title: 'Reset password',
      message: warning
        + `Reset the password for ${email}?\n\n`
        + 'This generates a NEW temporary password and invalidates their current one.',
      danger: Boolean(warning),
      confirmLabel: 'Reset password',
      onConfirm: async () => {
        setActionLoading(email + ':reset');
        try {
          const res = await axios.post(`${API}/admin/users/${encodeURIComponent(email)}/reset-password`);
          setModal({ email, tempPassword: res.data.temp_password });
          loadData(logFilter);
        } catch (err) {
          showToast(err.response?.data?.detail || 'Failed to reset password.', 'error');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  // PWWARN (confirmed decision by Marie-Lynne, after the end-to-end investigation): all three
  // controls below overwrite the account's password. For someone who has NEVER logged in that is
  // harmless - the temp password they never used is replaced by another. For someone ALREADY
  // USING the portal it silently destroys the password they chose, locking them out until they
  // find the email. The two cases were indistinguishable in the dialog, even though last_login is
  // already returned by GET /admin/users and already displayed in the same table row.
  //
  // NOT A BLOCK, DELIBERATELY: resending to an enrolled user is a LEGITIMATE action when they
  // have lost their password. This warns and names the date so the sender can tell the two cases
  // apart; it never refuses.
  //
  // ONE helper, used by all three controls, so their wording cannot drift.
  const userByEmail = (email) =>
    users.find(u => (u.email || '').toLowerCase() === (email || '').toLowerCase());

  // The extra paragraph shown ONLY when last_login is set. Returns '' otherwise, so the caller
  // composes the same message either way and the no-last_login wording is byte-unchanged.
  const pwOverwriteWarning = (email) => {
    const u = userByEmail(email);
    if (!u || !u.last_login) return '';
    return `WARNING: this person has already signed in (last sign-in ${fmt(u.last_login)}).\n\n`
      + 'They have set their own password, and it will STOP WORKING immediately. They will have '
      + 'to use the new temporary password from this email to get back in. Their MFA enrolment is '
      + 'not affected.\n\n';
  };

  // PWWARN: the interface half of the is_active block. The backend refuses regardless (that is the
  // real control); this stops the request being made at all and explains why, rather than letting
  // the user click and read an error.
  const isInactive = (email) => {
    const u = userByEmail(email);
    return !!u && u.is_active === false;
  };

  // RESENDWELCOME: distinct from Reset password. This re-sends the full welcome email (with a
  // fresh temporary password) rather than the short "your temporary password" note, so the
  // intent is explicit instead of Reset password being repurposed for it.
  // CONFIRMMODAL: the three window.confirm calls in this file became askConfirm - the styled
  // ConfirmModal with the exact same action running on confirm.
  const handleResendWelcome = (email) => {
    askConfirm({
      title: 'Resend welcome email (personal address)',
      // RESENDCOMPANY: previously "Resend the welcome email to ${email}", which named the LOGIN
      // address - not necessarily where it goes, since this path resolves personal first, then
      // delegate, then company. Wording only; the endpoint and the resolution are unchanged, and
      // the toast below still reports the address the server actually used.
      // PWWARN: the warning paragraph goes FIRST, before the routine explanation, so it is read
      // rather than skipped. It is empty for an account that has never signed in, leaving the
      // wording below byte-identical to what it was.
      message: pwOverwriteWarning(email)
        + `Resend the welcome email for ${email}?\n\n`
        + 'It goes to their PERSONAL address first, then delegate, then company. The toast will '
        + 'confirm which was used.\n\n'
        + 'This generates a NEW temporary password and invalidates their current one.',
      danger: Boolean(pwOverwriteWarning(email)),
      confirmLabel: 'Resend',
      onConfirm: async () => {
        setActionLoading(email + ':welcome');
        try {
          const res = await axios.post(`${API}/admin/users/${encodeURIComponent(email)}/resend-welcome`);
          showToast(`Welcome email sent to ${res.data.sent_to || email}.`, 'success');
          loadData(logFilter);
        } catch (err) {
          showToast(err.response?.data?.detail || 'Failed to resend welcome email.', 'error');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  // RESENDCOMPANY (confirmed decision by Marie-Lynne): the SAME welcome email, sent explicitly to
  // the COMPANY address. Separate from handleResendWelcome above, which is untouched and still
  // resolves personal first - correct at creation, wrong months later when the personal address
  // may be stale and the company mailbox now exists.
  //
  // The confirm names the DESTINATION rather than the login address: the two buttons differ only
  // in where they send, so a modal that did not say which would make them indistinguishable at
  // the moment of deciding. It repeats the same password-invalidation warning the personal resend
  // already carries, since both overwrite it and pressing one after the other leaves only the
  // second working.
  // WELCOMECOMBINED (confirmed decision by Marie-Lynne): "Welcome company" replaces the former
  // Resend welcome (company). It still sends the welcome email to the COMPANY address with a new
  // temporary password, and now ALSO resets MFA - but only when the account is actually enrolled.
  //
  // THE DIALOG MUST DISTINGUISH TWO CASES, because the consequence genuinely differs and the
  // MFAALWAYS (confirmed decision by Marie-Lynne, reversing the two-case dialog built earlier the
  // same night): MFA is now reset EVERY time this action runs, so there are no longer two cases to
  // distinguish. The dialog states the one outcome plainly, including for an account partway
  // through enrolment whose unscanned QR this destroys.
  //
  // ALWAYS `danger`, no longer conditional on enrolment: the action now always destroys MFA state
  // as well as the password, so it always carries the destructive styling.
  const handleResendWelcomeCompany = (email) => {
    const opt = { resetMfa: false };
    askConfirm({
      title: 'Welcome company',
      // PWWARN: same warning, same helper, so this dialog cannot say something different from the
      // other credential controls about the password consequence they share.
      message: pwOverwriteWarning(email)
        + 'Send the welcome email to this employee\'s COMPANY address?\n\n'
        + 'It goes to the company address only, with no fallback to the personal one.\n\n'
        + 'This generates a NEW temporary password and invalidates their current one, including '
        + 'any password a previous resend issued.\n\n'
        + adminT('adminMfa.resetOptionHelp'),
      // FIRSTLOGIN rule 7: MFA is reset ONLY when this box is ticked (off by default).
      extra: <ResetMfaOption onChange={(v) => { opt.resetMfa = v; }} />,
      danger: true,
      confirmLabel: 'Send',
      onConfirm: async () => {
        setActionLoading(email + ':welcomecompany');
        try {
          const res = await axios.post(
            `${API}/admin/users/${encodeURIComponent(email)}/resend-welcome-company`
            + (opt.resetMfa ? '?reset_mfa=true' : ''));
          // Reports the EXACT recipient the server used, not the login address, so the sender can
          // see where it actually went. MFAALWAYS: the MFA half is still read from the SERVER's
          // own `mfa_reset` rather than assumed here, so if the server ever stops resetting, the
          // toast stops claiming it did.
          const outcome = res.data.mfa_reset
            ? ` Password and MFA were reset. ${adminT('adminMfa.resetToldUser')}`
            : ' Password was reset. MFA was kept.';
          showToast(`Welcome email sent to company address ${res.data.sent_to || email}.${outcome}`, 'success');
          loadData(logFilter);
        } catch (err) {
          showToast(err.response?.data?.detail || 'Failed to send the welcome email to the company address.', 'error');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleToggleActive = async (email) => {
    setActionLoading(email + ':toggle');
    try {
      await axios.post(`${API}/admin/users/${encodeURIComponent(email)}/toggle-active`);
      loadData(logFilter);
    } catch (err) {
      showToast(ccoErrorText(err.response?.data?.detail, adminT) || 'Failed to update user.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetMfa = (email) => {
    askConfirm({
      title: 'Reset MFA',
      // FIRSTLOGIN: the admin confirmation carries the same guidance the person will need.
      message: `Reset MFA for ${email}? They will need to re-scan the QR code on next login.\n\n`
        + adminT('mfaGuide.beforeScan'),
      confirmLabel: 'Reset MFA',
      danger: true,
      onConfirm: async () => {
        setActionLoading(email + ':mfa');
        try {
          await axios.post(`${API}/admin/users/${encodeURIComponent(email)}/reset-mfa`);
          showToast('MFA reset successfully', 'success');
          loadData(logFilter);
        } catch (err) {
          showToast(err.response?.data?.detail || 'Failed to reset MFA.', 'error');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleChangeRole = (email, newRole, oldRole) => {
    // CCO: the compliance role is given with its entities, in one request, from the editor.
    if (newRole === 'compliance' && oldRole !== 'compliance') {
      setScopeEditor({ email, mode: 'role', selected: [] });
      return;
    }
    askConfirm({
      title: 'Change role',
      message: `Change role for ${email} from "${oldRole}" to "${newRole}"?`,
      confirmLabel: 'Change role',
      onConfirm: async () => {
        setActionLoading(email + ':role');
        try {
          await axios.post(`${API}/admin/users/${encodeURIComponent(email)}/change-role`, { role: newRole });
          loadData(logFilter);
        } catch (err) {
          showToast(ccoErrorText(err.response?.data?.detail, adminT) || 'Failed to change role.', 'error');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  // CCO: edit the entities of a compliance account (mode 'scope'), or give the compliance role with
  // its entities (mode 'role'). Empty makes the CCO; the server refuses a second one.
  const handleEditCompliance = (user) => {
    setScopeEditor({ email: user.email, mode: 'scope', selected: user.compliance_entities || [] });
  };
  const saveScopeEditor = async () => {
    if (!scopeEditor) return;
    setScopeSaving(true);
    const email = encodeURIComponent(scopeEditor.email);
    try {
      if (scopeEditor.mode === 'role') {
        await axios.post(`${API}/admin/users/${email}/change-role`,
                         { role: 'compliance', entities: scopeEditor.selected });
      } else {
        await axios.post(`${API}/admin/users/${email}/compliance-scope`, { entities: scopeEditor.selected });
      }
      setScopeEditor(null);
      loadData(logFilter);
    } catch (err) {
      showToast(ccoErrorText(err.response?.data?.detail, adminT) || adminT('adminCco.saveFailed'), 'error');
    } finally {
      setScopeSaving(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      // CCO: a compliance account carries its entities; every other role posts exactly as before.
      const body = createForm.role === 'compliance' ? { ...createForm, entities: createEntities } : createForm;
      const res = await axios.post(`${API}/admin/users`, body);
      setModal({ email: res.data.email, tempPassword: res.data.temp_password });
      setShowCreateForm(false);
      setCreateForm({ email: '', role: 'employee' });
      setCreateEntities([]);
      loadData(logFilter);
    } catch (err) {
      showToast(ccoErrorText(err.response?.data?.detail, adminT) || 'Failed to create account.', 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSchedulerTrigger = async (key, path, method = 'get') => {
    setSchedulerStatus(prev => ({ ...prev, [key]: { loading: true, result: null } }));
    try {
      await axios({ method, url: `${API}${path}` });
      setSchedulerStatus(prev => ({ ...prev, [key]: { loading: false, result: 'ok' } }));
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setSchedulerStatus(prev => ({ ...prev, [key]: { loading: false, result: 'error', error: detail } }));
    }
  };

  const handleExpandUser = async (user) => {
    if (expandedUser === user.email) { setExpandedUser(null); return; }
    setExpandedUser(user.email);
    setDelegateEmail('');
    if (user.employee_id) {
      try {
        const res = await axios.get(`${API}/employees/${user.employee_id}`);
        setDelegateEmail(res.data.delegate_email || '');
      } catch { /* leave empty */ }
    }
  };

  const handleSaveDelegateEmail = async (user) => {
    if (!user.employee_id) return;
    setSavingDelegate(true);
    try {
      await axios.patch(`${API}/employees/${user.employee_id}/delegate-email`, { delegate_email: delegateEmail || null });
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update delegate email.', 'error');
    } finally {
      setSavingDelegate(false);
    }
  };

  const handleResendDelegateNotification = async (user) => {
    if (!user.employee_id) return;
    setResendingDelegate(true);
    try {
      const res = await axios.post(`${API}/employees/${user.employee_id}/delegate-email/resend`);
      showToast(`Notification sent to ${res.data.sent_to}`, 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not resend notification.', 'error');
    } finally {
      setResendingDelegate(false);
    }
  };

  const fmt = (dt) => {
    if (!dt) return '-';
    return toDisplayDate(dt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
  };

  // FPFIX: My Profile is the employee view itself, a full page with its own top bar, exactly as
  // the COO's My Profile (Dashboard.js CooProfileView); a back bar returns to the admin pages.
  if (activeSection === 'profile' && myEmployee) {
    return (
      <>
        <div style={{ padding: '10px 20px', background: '#f8f6f3', borderBottom: '1px solid #e2e6ec' }}>
          <button type="button" onClick={() => navigate('/admin')}
            style={{ fontSize: '12px', color: NAVY_SHELL, border: '1px solid #d0d5dd', borderRadius: '6px',
                     padding: '5px 12px', cursor: 'pointer', background: 'none' }}>
            {adminT('adminNav.backToAdmin')}
          </button>
        </div>
        <EmployeeView
          employeeId={myEmployee.id}
          currentUser={currentUser}
          onLogout={onLogout}
          userRole="admin"
          employeeEntity={myEmployee.primary_entity}
          onLogoClick={(resetToOverview) => resetToOverview()}
        />
      </>
    );
  }

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: '#f8f9fa' }}>
      {confirmModal}

      {/* ── Modal overlay (position:fixed, unaffected by page structure) ── */}
      {modal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h3 style={s.modalTitle}>Temporary password</h3>
            <p style={s.modalEmail}>{modal.email}</p>
            <code style={s.tempPwd}>{modal.tempPassword}</code>
            <p style={s.modalNote}>
              Share this password securely. The user will be prompted to change it on next login.
            </p>
            <button style={s.btn} onClick={() => setModal(null)}>Close</button>
          </div>
        </div>
      )}

      {/* ── Sticky navy topbar ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', backgroundColor: NAVY_SHELL, borderBottom: '0.5px solid rgba(255,255,255,0.1)', position: 'sticky', top: 0, zIndex: 10 }}>
        <button
          type="button"
          style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left' }}
          onClick={() => navigate(ROLE_DEFAULTS.admin)}
          title="Go to dashboard"
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', border: `1.5px solid ${GOLD}`, color: GOLD, borderRadius: '6px', fontSize: '15px', fontWeight: '700' }}>Σ</span>
          <span style={{ display: 'block' }}>
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#fff' }}>Enko Portal</span>
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}> · Admin Panel</span>
          </span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* TOPBARNAME: name, falling back to the email for an account with no employee record. */}
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginRight: '4px' }}>{topBarIdentity || currentUser}</span>
          <TopbarBtn onClick={onLogout}>Sign out</TopbarBtn>
        </div>
      </div>

      {/* DUALWARN: one banner per configuration warning the server reports. */}
      {configWarnings.includes('dual_entity_unmatched') && (
        <div role="alert" style={{ background: '#fff4e5', borderBottom: '1px solid #f0c27b', color: '#7a4b00', padding: '10px 24px', fontSize: '13px' }}>
          <strong>{adminT('adminWarnings.dualEntityUnmatchedTitle')}</strong>{' '}
          {adminT('adminWarnings.dualEntityUnmatchedBody')}
        </div>
      )}

      {/* ADMINPAGES: sidebar + routed content, matching Dashboard.js's shell. Light mode only
          (confirmed decision) - DarkModeContext is deliberately NOT wired in here. */}
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 53px)' }}>
        <div style={T.sidebar}>
          <NavGroup label="ADMIN" groupKey="admin" dark={false}>
            {[...NAV_ITEMS.slice(0, -1), VALIDATION_NAV, ...(myEmployee ? [PROFILE_NAV] : []),
              ...NAV_ITEMS.slice(-1)].map(item => (
              <button
                key={item.id}
                style={activeSection === item.id ? T.navItemActive : T.navItem}
                onClick={() => navigate(item.path)}
              >
                <span style={{ flex: 1 }}>{item.labelKey ? adminT(item.labelKey) : item.label}</span>
              </button>
            ))}
          </NavGroup>
        </div>

        <div style={{ flex: 1, minWidth: 0, padding: '24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {error && <div style={s.errorBox}>{error}</div>}

      {loading ? (
        <p style={s.empty}>Loading...</p>
      ) : (
        <>          {activeSection === 'users' && (
            <UserAccountsPage
              s={s} users={users}
              showCreateForm={showCreateForm} setShowCreateForm={setShowCreateForm}
              createForm={createForm} setCreateForm={setCreateForm}
              createLoading={createLoading} handleCreateAccount={handleCreateAccount}
              actionLoading={actionLoading}
              handleResetPassword={handleResetPassword}
              handleResendWelcome={handleResendWelcome}
              handleResendWelcomeCompany={handleResendWelcomeCompany}
              handleToggleActive={handleToggleActive}
              handleResetMfa={handleResetMfa}
              handleChangeRole={handleChangeRole}
              handleExpandUser={handleExpandUser} expandedUser={expandedUser}
              delegateEmail={delegateEmail} setDelegateEmail={setDelegateEmail}
              savingDelegate={savingDelegate} handleSaveDelegateEmail={handleSaveDelegateEmail}
              resendingDelegate={resendingDelegate}
              handleResendDelegateNotification={handleResendDelegateNotification}
              fmt={fmt}
              ccoT={adminT} complianceEntities={complianceEntities}
              createEntities={createEntities} setCreateEntities={setCreateEntities}
              scopeEditor={scopeEditor} setScopeEditor={setScopeEditor}
              saveScopeEditor={saveScopeEditor} scopeSaving={scopeSaving}
              handleEditCompliance={handleEditCompliance}
            />
          )}

          {activeSection === 'audit' && (
            <AuditLogPage
              s={s} logs={logs} logTotal={logTotal}
              logFilter={logFilter} setLogFilter={setLogFilter}
              logActionTypes={logActionTypes} logsLoading={logsLoading} logsFailed={logsFailed} loadLogs={loadLogs}
            />
          )}

          {activeSection === 'jobruns' && (
            <JobRunsPage
              s={s} jobRuns={jobRuns} jobRunsLoading={jobRunsLoading}
              jobRunsError={jobRunsError} loadJobRuns={loadJobRuns} fmt={fmt}
            />
          )}

          {activeSection === 'validation' && <ValidationView />}

          {activeSection === 'scheduler' && (
            <SchedulerPage
              s={s} schedulerStatus={schedulerStatus}
              handleSchedulerTrigger={handleSchedulerTrigger}
            />
          )}
        </>
      )}
        </div>
        </div>
      </div>
    </div>
  );
}

function roleColor(role) {
  const map = {
    admin:      { backgroundColor: NAVY_FORM, color: '#fff' },
    coo:        { backgroundColor: '#2c3e50', color: '#fff' },
    hr:         { backgroundColor: '#e8f4fd', color: '#2980b9' },
    technology: { backgroundColor: '#edfaf1', color: '#27ae60' },
    compliance: { backgroundColor: '#fef9e7', color: '#f39c12' },
    finance:    { backgroundColor: '#e8f6f3', color: '#16a085' },
    employee:   { backgroundColor: '#f0f0f0', color: '#666' },
  };
  return map[role] || map.employee;
}

const s = {
  container:   { fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: '1200px', margin: '0 auto', padding: '24px', backgroundColor: '#f8f9fa' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' },
  title:       { fontSize: '28px', fontWeight: '600', color: NAVY_FORM, margin: '0' },
  subtitle:    { fontSize: '16px', color: '#666', margin: '4px 0 0 0' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  email:       { fontSize: '13px', color: '#888' },
  signOutBtn:  { backgroundColor: 'transparent', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 12px', fontSize: '13px', cursor: 'pointer', color: '#333' },
  section:     { backgroundColor: '#fff', borderRadius: '8px', padding: '24px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  sectionTitle:{ fontSize: '18px', fontWeight: '600', color: NAVY_FORM, margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' },
  badge:       { backgroundColor: NAVY_FORM, color: '#fff', borderRadius: '12px', padding: '2px 8px', fontSize: '12px' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { textAlign: 'left', padding: '12px', borderBottom: '2px solid #eee', fontSize: '13px', color: '#666', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  tr:          { borderBottom: '1px solid #eee' },
  td:          { padding: '12px', fontSize: '14px', color: '#333', whiteSpace: 'nowrap' },
  logCell:     { padding: '10px 12px', fontSize: '13px', color: '#333', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  roleBadge:   { borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '500' },
  roleSelect:  { borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '500', border: '1px solid transparent', cursor: 'pointer', outline: 'none' },
  activeChip:  { backgroundColor: '#edfaf1', color: '#27ae60', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '500' },
  inactiveChip:{ backgroundColor: '#fde8e8', color: '#e74c3c', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '500' },
  // MFACOLUMN: same chip shape as activeChip/inactiveChip above. Green/amber/grey follows the
  // status-colour convention already used across the app (#2D4A3E green, #c9a84c gold, grey
  // neutral) rather than introducing new hues.
  mfaEnrolledChip: { backgroundColor: '#edfaf1', color: '#27ae60', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' },
  mfaPendingChip:  { backgroundColor: '#fef8e7', color: '#8B6914', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' },
  mfaNoneChip:     { backgroundColor: '#f0f0f0', color: '#888',    borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' },
  actionBtn:   { backgroundColor: NAVY_FORM, color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '13px', cursor: 'pointer', marginRight: '8px' },
  deactivateBtn:{ backgroundColor: '#fde8e8', color: '#e74c3c', border: '1px solid #e74c3c', backgroundColor: 'transparent' },
  resetMfaBtn: { backgroundColor: 'transparent', color: '#e74c3c', border: '1px solid #e74c3c' },
  activateBtn: { backgroundColor: '#edfaf1', color: '#27ae60', border: '1px solid #27ae60', backgroundColor: 'transparent' },
  empty:       { color: '#888', fontStyle: 'italic', fontSize: '14px' },
  createForm:  { display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', flexWrap: 'wrap' },
  label:       { display: 'block', fontSize: '11px', color: '#666', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input:       { padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none', minWidth: '200px' },
  errorBox:    { backgroundColor: '#fde8e8', border: '1px solid #e74c3c', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '14px', color: '#c0392b' },
  overlay:     { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:       { backgroundColor: '#fff', borderRadius: '12px', padding: '32px', width: '420px', maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', textAlign: 'center' },
  modalTitle:  { fontSize: '18px', fontWeight: '600', color: NAVY_FORM, margin: '0 0 8px 0' },
  modalEmail:  { fontSize: '13px', color: '#888', margin: '0 0 16px 0' },
  tempPwd:     { display: 'block', fontFamily: 'monospace', fontSize: '18px', letterSpacing: '0.1em', color: NAVY_FORM, backgroundColor: '#f0f4ff', borderRadius: '8px', padding: '14px', marginBottom: '12px', wordBreak: 'break-all' },
  modalNote:   { fontSize: '13px', color: '#888', margin: '0 0 20px 0' },
  btn:         { backgroundColor: NAVY_FORM, color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', cursor: 'pointer' },
};

export default AdminPanel;

// FIRSTLOGIN: "MFA enrolled on <date>, last verified <date>", so an admin can tell an old
// authenticator entry from a real problem before resetting.
function MfaDates({ user }) {
  const { t } = useTranslation();
  if (!user || !user.mfa_enrolled_at) return null;
  return (
    <div data-testid="mfa-dates" style={{ fontSize: '11px', color: '#667085', marginTop: '4px' }}>
      {t('adminMfa.enrolledLine', {
        enrolled: user.mfa_enrolled_at,
        verified: user.mfa_last_verified_at || t('adminMfa.never'),
      })}
    </div>
  );
}

// FIRSTLOGIN rule 7: the opt-in for resetting MFA with a company welcome resend. Off by default.
function ResetMfaOption({ onChange }) {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(false);
  return (
    <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', margin: '0 0 16px' }}>
      <input type="checkbox" data-testid="reset-mfa-option" checked={checked}
             onChange={(e) => { setChecked(e.target.checked); onChange(e.target.checked); }} />
      <span>{t('adminMfa.alsoReset')}</span>
    </label>
  );
}
